import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import Button from "@components/ui/buttons/Button";
import { useState, useEffect } from "react";
import { Project, ProjectDocument, SignatureRequest } from "types";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { projectsApi } from "@services/api/projects";
import { signatureRequestsApi } from "@services/api/signatureRequests";
import { SignatureStatusModal } from "@components/modals/SignatureStatusModal";
import { useDataStore } from "@store/user_org";

const getDocumentById = (
  project: Project,
  documentId: string,
): ProjectDocument | undefined => {
  for (const folder of project.folders) {
    const doc = folder.documents?.find((d) => d.id === documentId);
    if (doc) return doc;
    if (folder.sub_folders) {
      for (const sub of folder.sub_folders) {
        const subDoc = sub.documents?.find((d) => d.id === documentId);
        if (subDoc) return subDoc;
      }
    }
  }
  return undefined;
};

const updateDocumentInProject = (
  project: Project,
  documentId: string,
  updater: (doc: ProjectDocument) => ProjectDocument,
): Project => {
  return {
    ...project,
    folders: project.folders.map((folder) => ({
      ...folder,
      documents: folder.documents?.map((d) =>
        d.id === documentId ? updater(d) : d,
      ),
      sub_folders: folder.sub_folders?.map((sub) => ({
        ...sub,
        documents: sub.documents?.map((d) =>
          d.id === documentId ? updater(d) : d,
        ),
      })),
    })),
  };
};

const removeDocumentFromProject = (
  project: Project,
  documentId: string,
): Project => {
  return {
    ...project,
    folders: project.folders.map((folder) => ({
      ...folder,
      documents: folder.documents?.filter((d) => d.id !== documentId),
      sub_folders: folder.sub_folders?.map((sub) => ({
        ...sub,
        documents: sub.documents?.filter((d) => d.id !== documentId),
      })),
    })),
  };
};

interface DocumentModalProps {
  onClose: () => void;
  document_id: string;
  activeProject: Project;
  setActiveProject?: (project: Project) => void;
  readOnly?: boolean;
  hideSignatures?: boolean;
  onRequestSignature?: (doc: ProjectDocument) => void;
  signatureRequests?: SignatureRequest[];
}

export const DocumentModal = ({
  onClose,
  document_id,
  activeProject,
  setActiveProject,
  readOnly = false,
  hideSignatures = false,
  onRequestSignature,
  signatureRequests: externalSignatureRequests,
}: DocumentModalProps) => {
  const userData = useDataStore((s) => s.userData);
  const [document, setDocument] = useState<ProjectDocument | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(true);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [fetchedSignatureRequests, setFetchedSignatureRequests] = useState<
    SignatureRequest[]
  >([]);
  const [signatureStatusOpen, setSignatureStatusOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    projectsApi
      .get(activeProject.id)
      .then((freshProject) => {
        const freshDoc = getDocumentById(freshProject, document_id);
        if (freshDoc) {
          setDocument(freshDoc);
        } else {
          setDocument(getDocumentById(activeProject, document_id));
        }
      })
      .catch(() => {
        setDocument(getDocumentById(activeProject, document_id));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [document_id, activeProject.id]);

  useEffect(() => {
    if (externalSignatureRequests || !activeProject?.id) return;
    signatureRequestsApi
      .listForProject(activeProject.id)
      .then(setFetchedSignatureRequests)
      .catch(() => {});
  }, [activeProject?.id, externalSignatureRequests]);

  // Derive existing signature request from available data (no async matching).
  // Priority: project_document_id (stable), then file id fallback for legacy
  // requests. Never fall back to filename — that caused signatures to land on
  // the wrong doc when names matched.
  const allSignatureRequests =
    externalSignatureRequests || fetchedSignatureRequests;
  const existingSignatureRequest = document
    ? allSignatureRequests.find((r) => {
        if (
          r.project_document_id &&
          document.id &&
          r.project_document_id === document.id
        ) {
          return true;
        }
        const docFileId = document.file?.id || "";
        if (!docFileId) return false;
        return (
          r.original_file?.id === docFileId ||
          r.current_file?.id === docFileId
        );
      }) || null
    : null;

  const userEmail = (userData?.infos?.email || "").toLowerCase();
  const pendingSignerForMe =
    existingSignatureRequest && existingSignatureRequest.status !== "completed"
      ? existingSignatureRequest.signers.find(
          (s) =>
            s.status === "pending" &&
            !!s.sign_url &&
            (s.signer_email || "").toLowerCase() === userEmail,
        ) || null
      : null;

  function handleToggleHidden() {
    if (!document || !setActiveProject) return;
    const updatedDoc = { ...document, hidden: !document.hidden };
    setDocument(updatedDoc);
    setActiveProject(
      updateDocumentInProject(activeProject, document.id, () => updatedDoc),
    );
  }

  function handleDelete() {
    if (!document || !setActiveProject) return;
    setActiveProject(removeDocumentFromProject(activeProject, document.id));
    onClose();
  }

  // Prefer the signature request's current PDF (prepared / signed) over the
  // project document's original file. The project doc's file is never mutated.
  const displayedPdfUrl =
    existingSignatureRequest?.current_file?.url || document?.file?.url || "";

  function handleOpenExternal() {
    if (displayedPdfUrl) window.open(displayedPdfUrl, "_blank");
  }

  async function handleDownload() {
    if (!displayedPdfUrl) return;
    try {
      const response = await fetch(displayedPdfUrl);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = blobUrl;
      link.download = document?.file?.name || document?.name || "document.pdf";
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(displayedPdfUrl, "_blank");
    }
  }

  if (!document && !loading) return null;

  const darkCircle =
    "w-[42px] h-[42px] rounded-full bg-neutral-900 hover:bg-neutral-700 grid place-items-center shadow-[0_2px_6px_rgba(0,0,0,0.12)] hover:shadow-[0_6px_16px_rgba(0,0,0,0.25)] hover:-translate-y-0.5 active:translate-y-0 active:scale-95 transition-all duration-150 ease-out";

  const outlineBtn =
    "inline-flex items-center gap-2 px-3.5 py-2 rounded-[9px] border border-neutral-300 bg-white hover:bg-neutral-50 transition-colors text-sm font-medium text-neutral-900";

  const showSignaturesBtn =
    !readOnly &&
    !hideSignatures &&
    document?.file?.id &&
    existingSignatureRequest &&
    existingSignatureRequest.status !== "completed";

  const showSignerBtn = !!(pendingSignerForMe && pendingSignerForMe.sign_url);

  const showRequestSignatureBtn =
    !readOnly &&
    !hideSignatures &&
    document?.file?.id &&
    !existingSignatureRequest &&
    !document.file.id.startsWith("generated_documents/") &&
    !document.file.id.startsWith("signature_requests/") &&
    !!onRequestSignature;

  return (
    <BaseModal
      width="max-w-[1040px]"
      title={document?.name || "Document"}
      startIcon={<Icon1 name="file" size={20} />}
      onClose={onClose}
    >
      {confirmationOpen && (
        <ConfirmationModal
          onClose={() => setConfirmationOpen(false)}
          onConfirm={handleDelete}
          title="Supprimer le document"
          message="Êtes-vous sûr de vouloir supprimer ce document ?"
          confirmText="CONFIRMER"
        />
      )}
      {signatureStatusOpen && existingSignatureRequest && (
        <SignatureStatusModal
          onClose={() => setSignatureStatusOpen(false)}
          signatureRequest={existingSignatureRequest}
        />
      )}

      <div className="flex flex-col bg-white">
        {/* Viewer */}
        <div className="px-6 pt-4 pb-1">
          <div
            className="relative w-full bg-[#f3f3f1] rounded-[10px] overflow-hidden border border-neutral-200"
            style={{
              height: "calc(90vh - 260px)",
              maxHeight: "640px",
              minHeight: "300px",
            }}
          >
            {loading || !document ? (
              <div className="w-full h-full flex items-center justify-center">
                <span className="text-sm text-neutral-400">Chargement...</span>
              </div>
            ) : (
              <iframe
                src={displayedPdfUrl}
                title={document.name}
                className="w-full h-full bg-white"
                style={{ border: "none" }}
              />
            )}

            {document?.hidden && (
              <div className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-neutral-700 bg-white/95 border border-neutral-300 rounded-full px-2.5 py-1 shadow-sm">
                <Icon1 name="invisible" size={12} />
                Masqué
              </div>
            )}
          </div>
        </div>

        {/* Controls */}
        {document && (
          <div className="px-6 pt-3 pb-3 flex flex-col items-center gap-2.5">
            <div className="inline-flex items-center gap-2 text-xs text-neutral-500">
              <span className="font-mono text-neutral-700 truncate max-w-[600px]">
                {document.file?.name || document.name}
              </span>
            </div>

            <div className="flex items-center gap-3">
              {!readOnly && (
                <button
                  onClick={handleToggleHidden}
                  className={darkCircle}
                  title={document.hidden ? "Afficher" : "Masquer"}
                >
                  <Icon1
                    name="invisible"
                    size={18}
                    color="white"
                  />
                </button>
              )}

              {!readOnly && (
                <button
                  onClick={() => setConfirmationOpen(true)}
                  className={darkCircle}
                  title="Supprimer le document"
                >
                  <Icon1 name="delete" size={18} color="white" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t-1 px-4 py-3 flex flex-row justify-between items-center gap-3">
        <div className="flex flex-row gap-2 flex-wrap">
          <button
            onClick={handleDownload}
            disabled={!displayedPdfUrl}
            className={`${outlineBtn} disabled:opacity-50 disabled:cursor-not-allowed`}
            title="Télécharger"
          >
            <Icon1 name="download" size={16} />
            Télécharger
          </button>

          <button
            onClick={handleOpenExternal}
            disabled={!displayedPdfUrl}
            className={`${outlineBtn} disabled:opacity-50 disabled:cursor-not-allowed`}
            title="Ouvrir dans un nouvel onglet"
          >
            <Icon1 name="open" size={16} />
            Ouvrir
          </button>

          {showSignaturesBtn && (
            <button
              onClick={() => setSignatureStatusOpen(true)}
              className={outlineBtn}
              title="Suivi de signature"
            >
              <Icon1 name="sign" size={16} />
              Signatures
            </button>
          )}

          {showRequestSignatureBtn && document && (
            <button
              onClick={() => {
                onRequestSignature!(document);
                onClose();
              }}
              className={outlineBtn}
              title="Demander une signature"
            >
              <Icon1 name="sign" size={16} />
              Demander signature
            </button>
          )}

          {showSignerBtn && (
            <button
              onClick={() =>
                window.open(pendingSignerForMe!.sign_url!, "_blank")
              }
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-[9px] bg-emerald-600 hover:bg-emerald-700 transition-colors text-sm font-semibold text-white shadow-[0_2px_6px_rgba(0,0,0,0.12)]"
              title="Signer le document"
            >
              <Icon1 name="sign" size={16} color="white" />
              Signer
            </button>
          )}
        </div>
        <Button variant="small" label="Fermer" onClick={onClose} />
      </div>
    </BaseModal>
  );
};
